/**
 * -------------------------------------------------------------------------------------------
 * Copyright (c) Microsoft Corporation.  All Rights Reserved.  Licensed under the MIT License.
 * See License in the project root for license information.
 * -------------------------------------------------------------------------------------------
 */

import { Context, Middleware } from '@microsoft/microsoft-graph-client';
import { getRequestHeader, setRequestHeader } from '@microsoft/microsoft-graph-client/lib/es/middleware/MiddlewareUtil';
import { ComponentMiddlewareOptions } from './ComponentMiddlewareOptions';

/**
 * Implements Middleware for the Graph sdk to inject
 * the toolkit version in the SdkVersion header
 *
 * @class SdkVersionMiddleware
 * @implements {Middleware}
 */
export class SdkVersionMiddleware implements Middleware {
  /**
   * @private
   * A member to hold next middleware in the middleware chain
   */
  private _nextMiddleware: Middleware;
  private _packageVersion: string;

  constructor(packageVersion: string) {
    this._packageVersion = packageVersion;
  }

  // tslint:disable-next-line: completed-docs
  public async execute(context: Context): Promise<void> {
    try {
      // Header parts must follow the format: 'name/version'
      const headerParts: string[] = [];

      const componentOptions = context.middlewareControl.getMiddlewareOptions(
        ComponentMiddlewareOptions
      ) as ComponentMiddlewareOptions;

      if (componentOptions) {
        const componentVersion: string = `${componentOptions.componentName}/${this._packageVersion}`;
        headerParts.push(componentVersion);
      }

      // Package version
      const packageVersion: string = `mgt/${this._packageVersion}`;
      headerParts.push(packageVersion);

      // Existing SdkVersion header value
      headerParts.push(getRequestHeader(context.request, context.options, 'SdkVersion'));

      // Join the header parts together and update the SdkVersion request header value
      const sdkVersionHeaderValue = headerParts.join(', ');
      setRequestHeader(context.request, context.options, 'SdkVersion', sdkVersionHeaderValue);
    } catch (error) {
      // ignore error
    }
    return await this._nextMiddleware.execute(context);
  }

  /**
   * Handles setting of next middleware
   *
   * @param {Middleware} next
   * @memberof SdkVersionMiddleware
   */
  public setNext(next: Middleware): void {
    this._nextMiddleware = next;
  }
}
